import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MonitoringLayout from "../components/MonitoringLayout";
import apiFetch from "../utils/apiFetch";
import UserContext from "../hooks/UserContext";
import SchemeModal from "../components/schemeModal/SchemeModal";

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";

const FIELD_LABELS = {
  foundation_cond: "Пойдевор ҳолати",
  foundation_nuts_tight: "Пойдевор гайкалари маҳкамлиги",
  base_washout: "Пойдевор ювилиши",
  soil_protect_obval: "Тупроқ ҳимояси (обвал)",
  earthing_kz_cond: "Заземление (КЗ) ҳолати",
  concrete_base_protect: "Бетон пойдевор ҳимояси",
  pole_tilt: "Таяначнинг қийшайиши",
  dispatcher_marks: "Диспетчер белгилари",
  angle_iron_cond: "Бурчак темир ҳолати",
  nuts_welded: "Гайкалар паяниши",
  bolts_thread_punched: "Болт резьбаси зарблиги",
  angle_iron_welded: "Бурчак темир паяниши",
  factory_welds_cond: "Завод пайвандлари ҳолати",
  leg_fit_to_found: "Оёқнинг пойдеворга ўтириши",
  guy_anbolts_cond: "Анкер болтлари ҳолати",
  cotter_pins: "Шплинтлар",
  insulators_cond: "Изолятор ҳолати",
  conductors_cond: "Ўтказгич ҳолати",
  shunt_cond: "Шунт ҳолати",
  sas_present: "САС мавжудлиги",
  thermovision_temp: "Термовизия ҳарорати",
  temp_sas_c: "ТМА ҳарорати (°C)",
  temp_nas_c: "НМА ҳарорати (°C)",
  pa_clamps_cond: "ПА қисқичлари ҳолати",
  thermoweld_present: "Термопайвандлаш мавжудлиги",
  damper_gvn_cond: "ГВН демпфери ҳолати",
  spacers_cond: "Дистанциялар ҳолати",
  gzt_present: "ГЗТ мавжудлиги",
  gzt_cond: "ГЗТ ҳолати",
  spark_gap_cond: "Учқун оралиғи ҳолати",
  gzt_earthing_cond: "ГЗТ заземлениеси ҳолати",
  vols_cond: "ВОЛС ҳолати",
  corridor_trees: "Йўлакда дарахтлар",
  corridor_reeds: "Йўлакда қамишлар",
  corridor_wheat: "Йўлакда буғдой",
  corridor_buildings: "Йўлакда иморатлар",
  clearance_ok: "Габарит масофаси",
  bird_guard_qqt: "Қуш ҳимояси (ҚҚТ)",
  cross_road: "Йўл кесишиши",
  cross_powerline: "Қувват линияси кесишиши",
  cross_river: "Дарё кесишиши",
  winter_ice: "Қишда муз",
};

const STATUS_LABELS = {
  MASTER_REVIEW: "Мастер кўриб чиқмоқда",
  LINE_CHIEF_REVIEW: "Тармоқ бошлиғи кўриб чиқмоқда",
  BRANCH_ENGINEER_REVIEW: "Бош муҳандис кўриб чиқмоқда",
  CLOSED: "Ёпилди",
  DISMISSED: "Рад этилди",
};

const STATUS_BADGE = {
  MASTER_REVIEW: "master",
  LINE_CHIEF_REVIEW: "chief",
  BRANCH_ENGINEER_REVIEW: "engineer",
  CLOSED: "closed",
  DISMISSED: "dismissed",
};

const MASTER_DEC_LABELS = {
  forward: "Бажариш",
  postpone: "Кейинга қолдириш",
};

const CHIEF_DEC_LABELS = {
  confirm: "Тасдиқлаш",
  reject: "Бажарилмади",
};

const CHIEF_POSTPONE_DEC_LABELS = {
  confirm: "Тасдиқлаш",
  reject: "Рад этиш",
};

const ENGINEER_DEC_LABELS = {
  confirm: "Тасдиқлаш",
  reject: "Рад этиш",
};

const ITEM_STATUS_LABELS = {
  open: "Фаол",
  closed: "Якунланди",
};

const ACTION_LABELS = {
  CREATED: "Корик яратилди",
  MASTER_SUBMIT: "Мастер қарор қабул қилди",
  CHIEF_SUBMIT: "Тармоқ бошлиғи қарор берди",
  ENGINEER_SUBMIT: "Бош муҳандис қарор берди",
  SLA_NOTIFICATION: "Муддат ўтиб кетди — огоҳлантириш",
  CLOSED: "Ёпилди",
  DISMISSED: "Рад этилди",
};

const ROLE_LABELS = {
  SYSTEM: "Тизим",
  MASTER: "Мастер",
  LINE_CHIEF: "Тармоқ бошлиғи",
  BRANCH_ENGINEER: "Бош муҳандис",
};

const ROLE_MOD = {
  SYSTEM: "system",
  MASTER: "master",
  LINE_CHIEF: "chief",
  BRANCH_ENGINEER: "engineer",
};

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Asia/Tashkent",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).format(d);
};

// ─── Пикер фото ───────────────────────────────────────────────────────────────
function PhotoPicker({ photos, onChange }) {
  const ref = useRef(null);
  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    onChange((prev) => [...prev, ...files].slice(0, 10));
    e.target.value = "";
  };
  const remove = (idx) => onChange((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="mon-photo-picker">
      <button type="button" className="mon-photo-add-btn" onClick={() => ref.current?.click()} disabled={photos.length >= 10}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        Фото қўшиш {photos.length > 0 && `(${photos.length}/10)`}
      </button>
      <input ref={ref} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={addFiles} />
      {photos.length > 0 && (
        <div className="mon-photo-thumbs">
          {photos.map((file, i) => (
            <div key={i} className="mon-photo-thumb">
              <img src={URL.createObjectURL(file)} alt="" />
              <button type="button" className="mon-photo-thumb__remove" onClick={() => remove(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Хелпер: проверка просроченности пункта ─────────────────────────────────
const isItemOverdue = (item, originalDueAt) => {
  if (item.item_status !== "open" && item.item_status) return false;
  const today = new Date(new Date().toDateString());
  // Есть подтверждённый postpone → проверяем postpone_date
  if (item.postpone_date && (item.postpone_approved_count || 0) >= 1) {
    return new Date(item.postpone_date) < today;
  }
  // Нет postpone → проверяем начальный срок original_due_at
  if (originalDueAt) {
    return new Date(originalDueAt) < new Date();
  }
  return false;
};

// Хелпер: дата дедлайна пункта (postpone_date или original_due_at)
const getItemDeadline = (item, originalDueAt) => {
  if (item.postpone_date && (item.postpone_approved_count || 0) >= 1) return item.postpone_date;
  return originalDueAt || null;
};

const formatPostponeDateGlobal = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }).format(dt);
};

// ─── Форма мастера ────────────────────────────────────────────────────────────
function MasterForm({ incident, onUpdated }) {
  const openItems = (incident.items || []).filter((i) => i.item_status === "open" || !i.item_status);
  const closedItems = (incident.items || []).filter((i) => i.item_status === "closed");

  const [decisions, setDecisions] = useState(() => {
    const init = {};
    openItems.forEach((item) => {
      if (isItemOverdue(item, incident.original_due_at)) {
        init[item.field_key] = { decision: "forward", comment: "", postpone_date: "" };
      } else if (item.master_decision) {
        init[item.field_key] = {
          decision: item.master_decision,
          comment: item.master_comment || "",
          postpone_date: item.postpone_date || "",
        };
      }
    });
    return init;
  });
  const [generalComment, setGeneralComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const setDecision = (k, d) => setDecisions((p) => ({ ...p, [k]: { ...p[k], decision: d } }));
  const setComment = (k, c) => setDecisions((p) => ({ ...p, [k]: { ...p[k], comment: c } }));
  const setPostponeDate = (k, date) => setDecisions((p) => ({ ...p, [k]: { ...p[k], postpone_date: date } }));

  const handleSubmit = async () => {
    const missing = openItems.filter((item) => !decisions[item.field_key]?.decision);
    if (missing.length > 0) {
      setSubmitError(`Барча пунктлар учун қарор танланг. Танланмаган: ${missing.map((i) => FIELD_LABELS[i.field_key] || i.field_key).join(", ")}`);
      return;
    }
    // Проверяем что у postpone пунктов есть дата
    const missingDate = openItems.filter(
      (item) => decisions[item.field_key]?.decision === "postpone" && !decisions[item.field_key]?.postpone_date
    );
    if (missingDate.length > 0) {
      setSubmitError(`Кейинга қолдирилган пунктлар учун сана танланг: ${missingDate.map((i) => FIELD_LABELS[i.field_key] || i.field_key).join(", ")}`);
      return;
    }
    setSubmitError("");
    const items = openItems.map((item) => ({
      field_key: item.field_key,
      decision: decisions[item.field_key]?.decision || null,
      comment: decisions[item.field_key]?.comment || "",
      postpone_date: decisions[item.field_key]?.postpone_date || null,
    }));
    const fd = new FormData();
    fd.append("items", JSON.stringify(items));
    fd.append("general_comment", generalComment);
    photos.forEach((f) => fd.append("photos", f));
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/incidents/${incident.id}/master-action`, { method: "POST", body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || "Хатолик"); }
      onUpdated(await res.json());
    } catch (e) {
      setSubmitError(e.message || "Юборишда хатолик");
    } finally {
      setSubmitting(false);
    }
  };

  const allDecided = openItems.every((item) => decisions[item.field_key]?.decision);

  // Минимальная дата для date picker — завтра
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  return (
    <>
      {openItems.map((item) => {
        const dec = decisions[item.field_key] || {};
        const overdue = isItemOverdue(item, incident.original_due_at);
        const deadline = getItemDeadline(item, incident.original_due_at);
        return (
          <div key={item.field_key} className={`mon-item${overdue ? " mon-item--overdue" : ""}`}>
            <div className="mon-item__top">
              <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
              {item.postpone_approved_count > 0 && (
                <span className="mon-dec-badge mon-dec-badge--postpone">
                  Узайтирилган: {item.postpone_approved_count} марта
                </span>
              )}
            </div>
            {overdue ? (
              <div className="mon-item__overdue-notice">
                ⏰ Муддати ўтган ({formatPostponeDateGlobal(deadline)})
              </div>
            ) : deadline ? (
              <div className="mon-item__deadline-notice">
                📅 Муддат: {formatPostponeDateGlobal(deadline)}
              </div>
            ) : null}
            {item.inspector_comment && (
              <div className="mon-item__inspector-comment">Инспектор изоҳи: {item.inspector_comment}</div>
            )}
            <div className="mon-item__decisions">
              {(overdue ? ["forward"] : ["forward", "postpone"]).map((d) => (
                <button key={d} type="button"
                  className={`mon-dec-btn mon-dec-btn--${d}` + (dec.decision === d ? " is-active" : "")}
                  onClick={() => setDecision(item.field_key, d)}>
                  {MASTER_DEC_LABELS[d]}
                </button>
              ))}
            </div>
            {dec.decision === "postpone" && (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontSize: 12, color: "#8b9ab5", display: "block", marginBottom: 4 }}>
                  Янги муддат:
                </label>
                <input
                  type="date"
                  min={minDate}
                  value={dec.postpone_date || ""}
                  onChange={(e) => setPostponeDate(item.field_key, e.target.value)}
                  className="mon-item__comment-input"
                  style={{ padding: "6px 10px", maxWidth: 200 }}
                />
              </div>
            )}
            <textarea className="mon-item__comment-input" placeholder="Изоҳ..."
              value={dec.comment || ""} onChange={(e) => setComment(item.field_key, e.target.value)} />
          </div>
        );
      })}

      {closedItems.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(45,52,84,0.5)" }}>
          <div style={{ fontSize: 12, color: "#8b9ab5", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Якунланган пунктлар
          </div>
          {closedItems.map((item) => (
            <div key={item.field_key} className="mon-item" style={{ opacity: 0.5 }}>
              <div className="mon-item__top">
                <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
                <span className="mon-dec-badge mon-dec-badge--forward">✓ Якунланди</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mon-action-footer" style={{ marginTop: 24 }}>
        <div>
          <div className="mon-action-footer__label">Умумий изоҳ</div>
          <textarea className="mon-action-footer__textarea" placeholder="Умумий изоҳ..."
            value={generalComment} onChange={(e) => setGeneralComment(e.target.value)} />
        </div>
        <PhotoPicker photos={photos} onChange={setPhotos} />
        {submitError && <div className="mon-error" style={{ marginTop: 0 }}>{submitError}</div>}
        <button type="button" className="mon-submit-btn" disabled={submitting || !allDecided}
          onClick={handleSubmit} title={!allDecided ? "Барча пунктлар учун қарор танланг" : ""}>
          {submitting ? "Юборилмоқда..." : "Қарорни тасдиқлаш"}
        </button>
      </div>
    </>
  );
}

// ─── Форма начальника линии ───────────────────────────────────────────────────
function ChiefForm({ incident, onUpdated }) {
  // Chief решает по open пунктам: forward + postpone с count < 1
  const chiefItems = (incident.items || []).filter(
    (i) => (i.item_status === "open" || !i.item_status) &&
           (i.master_decision === "forward" ||
            (i.master_decision === "postpone" && (i.postpone_approved_count || 0) < 1))
  );
  // Остальные open пункты (не для начальника — например postpone с count >= 1)
  const chiefKeys = new Set(chiefItems.map((i) => i.field_key));
  const otherOpenItems = (incident.items || []).filter(
    (i) => (i.item_status === "open" || !i.item_status) && !chiefKeys.has(i.field_key)
  );
  const closedItems = (incident.items || []).filter((i) => i.item_status === "closed");

  const [decisions, setDecisions] = useState(() => {
    const init = {};
    chiefItems.forEach((item) => {
      if (item.chief_decision) init[item.field_key] = { decision: item.chief_decision, comment: item.chief_comment || "" };
    });
    return init;
  });
  const [generalComment, setGeneralComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const setDecision = (k, d) => setDecisions((p) => ({ ...p, [k]: { ...p[k], decision: d } }));
  const setComment = (k, c) => setDecisions((p) => ({ ...p, [k]: { ...p[k], comment: c } }));

  const handleSubmit = async () => {
    const missing = chiefItems.filter((item) => !decisions[item.field_key]?.decision);
    if (missing.length > 0) {
      setSubmitError(`Барча пунктлар учун қарор танланг. Танланмаган: ${missing.map((i) => FIELD_LABELS[i.field_key] || i.field_key).join(", ")}`);
      return;
    }
    setSubmitError("");
    const items = chiefItems.map((item) => ({
      field_key: item.field_key,
      decision: decisions[item.field_key]?.decision || null,
      comment: decisions[item.field_key]?.comment || "",
    }));
    const fd = new FormData();
    fd.append("items", JSON.stringify(items));
    fd.append("general_comment", generalComment);
    photos.forEach((f) => fd.append("photos", f));
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/incidents/${incident.id}/chief-action`, { method: "POST", body: fd });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || "Хатолик"); }
      onUpdated(await res.json());
    } catch (e) {
      setSubmitError(e.message || "Юборишда хатолик");
    } finally {
      setSubmitting(false);
    }
  };

  const allDecided = chiefItems.every((item) => decisions[item.field_key]?.decision);

  const formatPostponeDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }).format(dt);
  };

  return (
    <>
      {chiefItems.map((item) => {
        const dec = decisions[item.field_key] || {};
        const isForward = item.master_decision === "forward";
        const isPostpone = item.master_decision === "postpone";
        const labels = isForward ? CHIEF_DEC_LABELS : CHIEF_POSTPONE_DEC_LABELS;

        const overdue = isItemOverdue(item, incident.original_due_at);
        const deadline = getItemDeadline(item, incident.original_due_at);
        return (
          <div key={item.field_key} className={`mon-item${overdue ? " mon-item--overdue" : ""}`}>
            <div className="mon-item__top">
              <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
              <span className={`mon-dec-badge mon-dec-badge--${item.master_decision}`}>
                Мастер: {MASTER_DEC_LABELS[item.master_decision]}
              </span>
            </div>
            {overdue && (
              <div className="mon-item__overdue-notice">
                ⏰ Муддати ўтган ({formatPostponeDate(deadline)})
              </div>
            )}
            {item.inspector_comment && <div className="mon-item__inspector-comment">Инспектор изоҳи: {item.inspector_comment}</div>}
            {item.master_comment && <div className="mon-item__inspector-comment">Мастер изоҳи: {item.master_comment}</div>}
            {isPostpone && item.postpone_date && (
              <div className="mon-item__inspector-comment">
                Сўралган муддат: <strong>{formatPostponeDate(item.postpone_date)}</strong>
              </div>
            )}
            <div className="mon-item__decisions">
              {["confirm", "reject"].map((d) => (
                <button key={d} type="button"
                  className={`mon-dec-btn mon-dec-btn--${d === "confirm" ? "forward" : "ignore"}` + (dec.decision === d ? " is-active" : "")}
                  onClick={() => setDecision(item.field_key, d)}>
                  {labels[d]}
                </button>
              ))}
            </div>
            <textarea className="mon-item__comment-input" placeholder="Изоҳ..."
              value={dec.comment || ""} onChange={(e) => setComment(item.field_key, e.target.value)} />
          </div>
        );
      })}

      {otherOpenItems.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(45,52,84,0.5)" }}>
          <div style={{ fontSize: 12, color: "#8b9ab5", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Бошқа фаол пунктлар
          </div>
          {otherOpenItems.map((item) => (
            <div key={item.field_key} className={`mon-item${isItemOverdue(item, incident.original_due_at) ? " mon-item--overdue" : ""}`} style={{ opacity: 0.65 }}>
              <div className="mon-item__top">
                <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
                {item.master_decision && (
                  <span className={`mon-dec-badge mon-dec-badge--${item.master_decision}`}>
                    Мастер: {MASTER_DEC_LABELS[item.master_decision] || item.master_decision}
                  </span>
                )}
                {item.postpone_approved_count > 0 && (
                  <span className="mon-dec-badge mon-dec-badge--postpone">
                    Узайтирилган: {item.postpone_approved_count} марта
                  </span>
                )}
              </div>
              {isItemOverdue(item, incident.original_due_at) && (
                <div className="mon-item__overdue-notice">
                  ⏰ Муддати ўтган ({formatPostponeDateGlobal(getItemDeadline(item, incident.original_due_at))})
                </div>
              )}
              {item.inspector_comment && <div className="mon-item__inspector-comment">Инспектор изоҳи: {item.inspector_comment}</div>}
              {item.master_comment && <div className="mon-item__inspector-comment">Мастер изоҳи: {item.master_comment}</div>}
              {!isItemOverdue(item, incident.original_due_at) && item.postpone_date && (
                <div className="mon-item__inspector-comment">
                  Сўралган муддат: <strong>{formatPostponeDate(item.postpone_date)}</strong>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {closedItems.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(45,52,84,0.5)" }}>
          <div style={{ fontSize: 12, color: "#8b9ab5", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Якунланган пунктлар
          </div>
          {closedItems.map((item) => (
            <div key={item.field_key} className="mon-item" style={{ opacity: 0.5 }}>
              <div className="mon-item__top">
                <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
                <span className="mon-dec-badge mon-dec-badge--forward">✓ Якунланди</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mon-action-footer" style={{ marginTop: 24 }}>
        <div>
          <div className="mon-action-footer__label">Умумий изоҳ</div>
          <textarea className="mon-action-footer__textarea" placeholder="Умумий изоҳ..."
            value={generalComment} onChange={(e) => setGeneralComment(e.target.value)} />
        </div>
        <PhotoPicker photos={photos} onChange={setPhotos} />
        {submitError && <div className="mon-error" style={{ marginTop: 0 }}>{submitError}</div>}
        <button type="button" className="mon-submit-btn" disabled={submitting || !allDecided}
          onClick={handleSubmit} title={!allDecided ? "Барча пунктлар учун қарор танланг" : ""}>
          {submitting ? "Юборилмоқда..." : "Қарорни тасдиқлаш"}
        </button>
      </div>
    </>
  );
}

// ─── Форма инженера (решение по продлению 2+ раз) ────────────────────────────
function EngineerForm({ incident, onUpdated }) {
  // Engineer решает только по postpone пунктам с postpone_approved_count >= 1
  const engineerItems = (incident.items || []).filter(
    (i) => (i.item_status === "open" || !i.item_status) &&
           i.master_decision === "postpone" &&
           (i.postpone_approved_count || 0) >= 1
  );
  // Остальные open пункты (не для инженера)
  const engineerKeys = new Set(engineerItems.map((i) => i.field_key));
  const otherOpenItems = (incident.items || []).filter(
    (i) => (i.item_status === "open" || !i.item_status) && !engineerKeys.has(i.field_key)
  );
  const closedItems = (incident.items || []).filter((i) => i.item_status === "closed");

  const [decisions, setDecisions] = useState(() => {
    const init = {};
    engineerItems.forEach((item) => {
      if (item.engineer_decision) {
        init[item.field_key] = { decision: item.engineer_decision, comment: item.engineer_comment || "" };
      }
    });
    return init;
  });
  const [generalComment, setGeneralComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const setDecision = (k, d) => setDecisions((p) => ({ ...p, [k]: { ...p[k], decision: d } }));
  const setComment = (k, c) => setDecisions((p) => ({ ...p, [k]: { ...p[k], comment: c } }));

  const formatPostponeDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }).format(dt);
  };

  const handleSubmit = async () => {
    const missing = engineerItems.filter((item) => !decisions[item.field_key]?.decision);
    if (missing.length > 0) {
      setSubmitError(`Барча пунктлар учун қарор танланг.`);
      return;
    }
    setSubmitError("");
    const items = engineerItems.map((item) => ({
      field_key: item.field_key,
      decision: decisions[item.field_key]?.decision || null,
      comment: decisions[item.field_key]?.comment || "",
    }));
    setSubmitting(true);
    try {
      const res = await apiFetch(`/api/incidents/${incident.id}/engineer-action`, {
        method: "POST",
        body: { items: JSON.stringify(items), general_comment: generalComment },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || "Хатолик"); }
      onUpdated(await res.json());
    } catch (e) {
      setSubmitError(e.message || "Юборишда хатолик");
    } finally {
      setSubmitting(false);
    }
  };

  const allDecided = engineerItems.every((item) => decisions[item.field_key]?.decision);

  return (
    <>
      <div className="mon-central-form__note mon-central-form__note--warn" style={{ marginBottom: 16 }}>
        ⚠ Мастер муддат узайтиришни сўрамоқда (такрорий)
      </div>

      {engineerItems.map((item) => {
        const dec = decisions[item.field_key] || {};
        const overdue = isItemOverdue(item, incident.original_due_at);
        return (
          <div key={item.field_key} className={`mon-item${overdue ? " mon-item--overdue" : ""}`}>
            <div className="mon-item__top">
              <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
              <span className="mon-dec-badge mon-dec-badge--postpone">
                Узайтирилган: {item.postpone_approved_count} марта
              </span>
            </div>
            {overdue && (
              <div className="mon-item__overdue-notice">
                ⏰ Муддати ўтган ({formatPostponeDate(getItemDeadline(item, incident.original_due_at))})
              </div>
            )}
            {item.inspector_comment && <div className="mon-item__inspector-comment">Инспектор изоҳи: {item.inspector_comment}</div>}
            {item.master_comment && <div className="mon-item__inspector-comment">Мастер изоҳи: {item.master_comment}</div>}
            {item.postpone_date && (
              <div className="mon-item__inspector-comment">
                Сўралган муддат: <strong>{formatPostponeDate(item.postpone_date)}</strong>
              </div>
            )}
            <div className="mon-item__decisions">
              {["confirm", "reject"].map((d) => (
                <button key={d} type="button"
                  className={`mon-dec-btn mon-dec-btn--${d === "confirm" ? "forward" : "ignore"}` + (dec.decision === d ? " is-active" : "")}
                  onClick={() => setDecision(item.field_key, d)}>
                  {ENGINEER_DEC_LABELS[d]}
                </button>
              ))}
            </div>
            <textarea className="mon-item__comment-input" placeholder="Изоҳ..."
              value={dec.comment || ""} onChange={(e) => setComment(item.field_key, e.target.value)} />
          </div>
        );
      })}

      {otherOpenItems.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(45,52,84,0.5)" }}>
          <div style={{ fontSize: 12, color: "#8b9ab5", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Бошқа фаол пунктлар
          </div>
          {otherOpenItems.map((item) => (
            <div key={item.field_key} className={`mon-item${isItemOverdue(item, incident.original_due_at) ? " mon-item--overdue" : ""}`} style={{ opacity: 0.65 }}>
              <div className="mon-item__top">
                <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
                {item.master_decision && (
                  <span className={`mon-dec-badge mon-dec-badge--${item.master_decision}`}>
                    Мастер: {MASTER_DEC_LABELS[item.master_decision] || item.master_decision}
                  </span>
                )}
              </div>
              {isItemOverdue(item, incident.original_due_at) && (
                <div className="mon-item__overdue-notice">
                  ⏰ Муддати ўтган ({formatPostponeDateGlobal(getItemDeadline(item, incident.original_due_at))})
                </div>
              )}
              {item.inspector_comment && <div className="mon-item__inspector-comment">Инспектор изоҳи: {item.inspector_comment}</div>}
              {item.master_comment && <div className="mon-item__inspector-comment">Мастер изоҳи: {item.master_comment}</div>}
            </div>
          ))}
        </div>
      )}

      {closedItems.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(45,52,84,0.5)" }}>
          <div style={{ fontSize: 12, color: "#8b9ab5", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Якунланган пунктлар
          </div>
          {closedItems.map((item) => (
            <div key={item.field_key} className="mon-item" style={{ opacity: 0.5 }}>
              <div className="mon-item__top">
                <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
                <span className="mon-dec-badge mon-dec-badge--forward">✓ Якунланди</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mon-action-footer" style={{ marginTop: 24 }}>
        <div>
          <div className="mon-action-footer__label">Умумий изоҳ</div>
          <textarea className="mon-action-footer__textarea" placeholder="Умумий изоҳ..."
            value={generalComment} onChange={(e) => setGeneralComment(e.target.value)} />
        </div>
        {submitError && <div className="mon-error" style={{ marginTop: 0 }}>{submitError}</div>}
        <button type="button" className="mon-submit-btn" disabled={submitting || !allDecided} onClick={handleSubmit}>
          {submitting ? "Юборилмоқда..." : "Қарорни тасдиқлаш"}
        </button>
      </div>
    </>
  );
}

// ─── Readonly пункты ──────────────────────────────────────────────────────────
function ItemsReadonly({ items }) {
  const openItems = (items || []).filter((i) => i.item_status === "open" || !i.item_status);
  const closedItems = (items || []).filter((i) => i.item_status === "closed");

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }).format(dt);
  };

  const renderItem = (item, faded) => (
    <div key={item.field_key} className="mon-item" style={faded ? { opacity: 0.5 } : {}}>
      <div className="mon-item__top">
        <span className="mon-item__label">{FIELD_LABELS[item.field_key] || item.field_key}</span>
        {item.item_status === "closed" && (
          <span className="mon-dec-badge mon-dec-badge--forward">✓ Якунланди</span>
        )}
        {item.postpone_approved_count > 0 && item.item_status !== "closed" && (
          <span className="mon-dec-badge mon-dec-badge--postpone">
            Узайтирилган: {item.postpone_approved_count} марта
          </span>
        )}
      </div>
      {item.inspector_comment && <div className="mon-item__inspector-comment">Инспектор изоҳи: {item.inspector_comment}</div>}
      {item.master_decision && (
        <div className="mon-item__master-answer" style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: "#8b9ab5", marginRight: 6 }}>Мастер:</span>
          <span className={`mon-dec-badge mon-dec-badge--${item.master_decision}`}>
            {MASTER_DEC_LABELS[item.master_decision] || item.master_decision}
          </span>
          {item.master_comment && <span className="mon-item__master-comment-text">{item.master_comment}</span>}
          {item.master_decision === "postpone" && item.postpone_date && (
            <span style={{ fontSize: 12, color: "#8b9ab5", marginLeft: 8 }}>
              → {formatDate(item.postpone_date)}
            </span>
          )}
        </div>
      )}
      {item.chief_decision && (
        <div className="mon-item__master-answer" style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: "#8b9ab5", marginRight: 6 }}>Тармоқ бошлиғи:</span>
          <span className={`mon-dec-badge mon-dec-badge--${item.chief_decision === "confirm" ? "forward" : "ignore"}`}>
            {item.master_decision === "forward"
              ? (CHIEF_DEC_LABELS[item.chief_decision] || item.chief_decision)
              : (CHIEF_POSTPONE_DEC_LABELS[item.chief_decision] || item.chief_decision)}
          </span>
          {item.chief_comment && <span className="mon-item__master-comment-text">{item.chief_comment}</span>}
        </div>
      )}
      {item.engineer_decision && (
        <div className="mon-item__master-answer">
          <span style={{ fontSize: 12, color: "#8b9ab5", marginRight: 6 }}>Бош муҳандис:</span>
          <span className={`mon-dec-badge mon-dec-badge--${item.engineer_decision === "confirm" ? "forward" : "ignore"}`}>
            {ENGINEER_DEC_LABELS[item.engineer_decision] || item.engineer_decision}
          </span>
          {item.engineer_comment && <span className="mon-item__master-comment-text">{item.engineer_comment}</span>}
        </div>
      )}
    </div>
  );

  return (
    <div className="mon-items">
      {openItems.map((item) => renderItem(item, false))}
      {closedItems.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(45,52,84,0.5)" }}>
          <div style={{ fontSize: 12, color: "#8b9ab5", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Якунланган пунктлар
          </div>
          {closedItems.map((item) => renderItem(item, true))}
        </div>
      )}
    </div>
  );
}

// ─── История (без циклов — плоский список) ──────────────────────────────────
function HistorySection({ updates, openGallery }) {
  if (!updates || updates.length === 0) return null;

  const fmtDateOnly = (v) => {
    if (!v) return "";
    const d = new Date(v.length === 10 ? v + "T00:00:00" : v);
    if (isNaN(d)) return v;
    return new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
  };

  return (
    <div className="mon-history">
      {updates.map((upd) => {
        const roleMod = ROLE_MOD[upd.actor_role] || "system";
        const roleLabel = ROLE_LABELS[upd.actor_role] || upd.actor_role;
        const actionLabel = ACTION_LABELS[upd.action] || upd.action;
        const authorName = upd.author_user
          ? `${upd.author_user.lastname || ""} ${upd.author_user.name || ""}`.trim()
          : "";
        const gc = upd.payload?.general_comment || upd.payload?.comment || "";
        const files = upd.files || [];

        return (
          <div key={upd.id} className="mon-history-entry">
            <div className={`mon-history-entry__dot mon-history-entry__dot--${roleMod}`}>
              {roleLabel.charAt(0)}
            </div>
            <div className="mon-history-entry__body">
              <div className="mon-history-entry__header">
                <span className="mon-history-entry__who">{authorName || roleLabel}</span>
                <span className="mon-history-entry__action">{actionLabel}</span>
                <span className="mon-history-entry__time">{formatDate(upd.date_created)}</span>
              </div>
              {gc && <div className="mon-history-entry__comment">{gc}</div>}

              {/* MASTER_SUBMIT — решения мастера по каждому пункту */}
              {upd.action === "MASTER_SUBMIT" && upd.payload?.items?.length > 0 && (
                <div className="mon-history-items">
                  {upd.payload.items.map((item) => (
                    <div key={item.field_key} className="mon-history-item-row">
                      <span className="mon-history-item-label">
                        {FIELD_LABELS[item.field_key] || item.field_key}
                      </span>
                      {item.decision && (
                        <span className={`mon-dec-badge mon-dec-badge--${item.decision}`}>
                          {MASTER_DEC_LABELS[item.decision] || item.decision}
                        </span>
                      )}
                      {item.decision === "postpone" && item.postpone_date && (
                        <span className="mon-history-item-date">→ {fmtDateOnly(item.postpone_date)}</span>
                      )}
                      {item.was_overdue && (
                        <span className="mon-history-overdue-tag">
                          ⏰ Муддати ўтган ({fmtDateOnly(item.overdue_deadline)})
                        </span>
                      )}
                      {item.comment && (
                        <span className="mon-history-item-comment">{item.comment}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* CHIEF_SUBMIT — решения тармоқ бошлиғи */}
              {upd.action === "CHIEF_SUBMIT" && upd.payload?.items?.length > 0 && (
                <div className="mon-history-items">
                  {upd.payload.items.map((item) => {
                    const hasMasterCtx = !!item.master_decision;
                    const isPostpone = item.master_decision === "postpone";
                    const decLabel = hasMasterCtx
                      ? (isPostpone
                          ? (CHIEF_POSTPONE_DEC_LABELS[item.decision] || item.decision)
                          : (CHIEF_DEC_LABELS[item.decision] || item.decision))
                      : (CHIEF_DEC_LABELS[item.decision] || item.decision);
                    const decMod = item.decision === "confirm" ? "forward" : "ignore";
                    return (
                      <div key={item.field_key} className="mon-history-item-row">
                        <span className="mon-history-item-label">
                          {FIELD_LABELS[item.field_key] || item.field_key}
                        </span>
                        <span className="mon-history-item-decisions">
                          {hasMasterCtx && (
                            <>
                              <span style={{ fontSize: 10, color: "#8b9ab5" }}>Мастер:</span>
                              <span className={`mon-dec-badge mon-dec-badge--${item.master_decision}`}>
                                {MASTER_DEC_LABELS[item.master_decision]}
                              </span>
                              {isPostpone && item.postpone_date && (
                                <span className="mon-history-item-date">→ {fmtDateOnly(item.postpone_date)}</span>
                              )}
                              <span style={{ color: "#2d3454", margin: "0 2px" }}>|</span>
                              <span style={{ fontSize: 10, color: "#8b9ab5" }}>Бошлиқ:</span>
                            </>
                          )}
                          {item.decision && (
                            <span className={`mon-dec-badge mon-dec-badge--${decMod}`}>
                              {decLabel}
                            </span>
                          )}
                        </span>
                        {item.was_overdue && (
                          <span className="mon-history-overdue-tag">
                            ⏰ Муддати ўтган — {item.delayed_by === "MASTER" ? "мастер кечикди" : "тармоқ бошлиғи кечикди"}
                          </span>
                        )}
                        {item.comment && (
                          <span className="mon-history-item-comment">{item.comment}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ENGINEER_SUBMIT — решения бош муҳандис */}
              {upd.action === "ENGINEER_SUBMIT" && upd.payload?.items?.length > 0 && (
                <div className="mon-history-items">
                  {upd.payload.items.map((item) => {
                    const decMod = item.decision === "confirm" ? "forward" : "ignore";
                    return (
                      <div key={item.field_key} className="mon-history-item-row">
                        <span className="mon-history-item-label">
                          {FIELD_LABELS[item.field_key] || item.field_key}
                        </span>
                        <span className="mon-history-item-decisions">
                          {item.decision && (
                            <span className={`mon-dec-badge mon-dec-badge--${decMod}`}>
                              {ENGINEER_DEC_LABELS[item.decision] || item.decision}
                            </span>
                          )}
                          {item.postpone_date && (
                            <span className="mon-history-item-date">→ {fmtDateOnly(item.postpone_date)}</span>
                          )}
                          {item.postpone_approved_count > 0 && (
                            <span style={{ fontSize: 10, color: "#8b9ab5" }}>
                              ({item.postpone_approved_count} марта)
                            </span>
                          )}
                        </span>
                        {item.was_overdue && (
                          <span className="mon-history-overdue-tag">
                            ⏰ Муддати ўтган — {item.delayed_by === "MASTER" ? "мастер кечикди" : "бош муҳандис кечикди"}
                          </span>
                        )}
                        {item.comment && (
                          <span className="mon-history-item-comment">{item.comment}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* SLA_NOTIFICATION — уведомление о просрочке */}
              {upd.action === "SLA_NOTIFICATION" && upd.payload && (
                <div className="mon-history-sla">
                  <span>⏰ Муддат ўтиб кетди</span>
                  {upd.payload.overdue_items?.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      {upd.payload.overdue_items.map((oi) => (
                        <div key={oi.field_key} style={{ fontSize: 12, marginLeft: 8 }}>
                          • {FIELD_LABELS[oi.field_key] || oi.field_key}
                          {oi.postpone_date && ` (${fmtDateOnly(oi.postpone_date)})`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {files.length > 0 && (
                <div className="mon-history-photos">
                  {files.map((f, idx) => (
                    <button key={f.id} type="button" className="mon-history-photo-btn"
                      onClick={() => openGallery(files, idx)}>
                      <img src={`${DIRECTUS_URL}/assets/${f.directus_files_id}?key=thumb-480`} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Главный компонент ────────────────────────────────────────────────────────
export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions } = useContext(UserContext);

  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [gallery, setGallery] = useState({ open: false, items: [], index: 0 });

  const canMasterAction = (permissions || []).includes("INCIDENT_MASTER_ACTION");
  const canChiefAction = (permissions || []).includes("INCIDENT_CHIEF_ACTION");
  const canEngineerAction = (permissions || []).includes("INCIDENT_ENGINEER_ACTION");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch(`/api/incidents/${id}`);
      if (!res.ok) { const data = await res.json().catch(() => ({})); throw new Error(data.message || "Хатолик"); }
      setIncident(await res.json());
    } catch (e) {
      if (e?.status === 401) navigate("/login");
      else if (e?.status === 403) navigate("/no-access");
      else setError(e.message || "Юкланмади");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const openGallery = (files, startIndex = 0) => {
    const items = files.map((f, i) => ({
      title: `Фото ${i + 1}`,
      full: `${DIRECTUS_URL}/assets/${f.directus_files_id}`,
    }));
    setGallery({ open: true, items, index: startIndex });
  };

  const showMasterForm = canMasterAction && incident?.status === "MASTER_REVIEW";
  const showChiefForm = canChiefAction && incident?.status === "LINE_CHIEF_REVIEW";
  const showEngineerForm = canEngineerAction && incident?.status === "BRANCH_ENGINEER_REVIEW";
  const isEditable = showMasterForm || showChiefForm || showEngineerForm;

  const formKey = incident?.last_action_at || "initial";

  return (
    <MonitoringLayout>
      {loading && <div className="mon-loading">Юкланмоқда...</div>}
      {error && !loading && <div className="mon-error">{error}</div>}

      {!loading && !error && incident && (
        <div className="mon-detail">
          <button type="button" className="mon-detail__back" onClick={() => navigate("/monitoring")}>
            ← Рўйхатга қайтиш
          </button>

          <div className="mon-detail__head">
            <div>
              <h1 className="mon-detail__title">
                Нуқсон #{incident.id} — {incident.pole?.name || "—"}
              </h1>
              <div className="mon-detail__meta">
                {incident.pole?.network?.name && (
                  <span className="mon-detail__meta-item">Линия: <span>{incident.pole.network.name}</span></span>
                )}
                {incident.branch?.name && (
                  <span className="mon-detail__meta-item">Филиал: <span>{incident.branch.name}</span></span>
                )}
                {incident.checkup?.performed_at && (
                  <span className="mon-detail__meta-item">Текшируви: <span>{formatDate(incident.checkup.performed_at)}</span></span>
                )}
                {incident.checkup?.inspector_user && (
                  <span className="mon-detail__meta-item">
                    Инспектор: <span>{incident.checkup.inspector_user.lastname || ""} {incident.checkup.inspector_user.name || ""}</span>
                  </span>
                )}
                {incident.checkup?.inspector_2_user && (
                  <span className="mon-detail__meta-item">
                    2-инспектор: <span>{incident.checkup.inspector_2_user.lastname || ""} {incident.checkup.inspector_2_user.name || ""}</span>
                  </span>
                )}
                {incident.due_at && (
                  <span className="mon-detail__meta-item">Муддат: <span>{formatDate(incident.due_at)}</span></span>
                )}
              </div>
            </div>
            <span className={`mon-badge mon-badge--${STATUS_BADGE[incident.status] || "master"}`}>
              {STATUS_LABELS[incident.status] || incident.status}
            </span>
            {incident.is_emergency && (
              <span className="mon-badge mon-badge--emergency">🚨 Авариявий</span>
            )}
          </div>

          {(incident.checkup?.notes || incident.checkup?.files?.length > 0) && (
            <div className="mon-card">
              <p className="mon-card__title">Кўрик маълумотлари</p>
              {incident.checkup?.notes && (
                <div className="mon-checkup-notes">{incident.checkup.notes}</div>
              )}
              {incident.checkup?.files?.length > 0 && (
                <div className="mon-history-photos" style={{ marginTop: incident.checkup.notes ? 10 : 0 }}>
                  {incident.checkup.files.map((f, idx) => (
                    <button
                      key={f.id}
                      type="button"
                      className="mon-history-photo-btn"
                      onClick={() => openGallery(incident.checkup.files, idx)}
                    >
                      <img src={`${DIRECTUS_URL}/assets/${f.directus_files_id}?key=thumb-480`} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {incident.items && incident.items.length > 0 && (
            <div className="mon-card">
              <p className="mon-card__title">
                {isEditable ? "Қарор қабул қилиш" : "Аниқланган муаммолар"}
              </p>

              {showMasterForm && (
                <div className="mon-items">
                  <MasterForm key={formKey} incident={incident} onUpdated={setIncident} />
                </div>
              )}
              {showChiefForm && (
                <div className="mon-items">
                  <ChiefForm key={formKey} incident={incident} onUpdated={setIncident} />
                </div>
              )}
              {showEngineerForm && (
                <div className="mon-items">
                  <EngineerForm key={formKey} incident={incident} onUpdated={setIncident} />
                </div>
              )}
              {!isEditable && (
                <ItemsReadonly items={incident.items} />
              )}
            </div>
          )}

          {incident.updates && incident.updates.length > 0 && (
            <div className="mon-card">
              <p className="mon-card__title">Тарих</p>
              <HistorySection updates={incident.updates} openGallery={openGallery} />
            </div>
          )}
        </div>
      )}

      <SchemeModal
        open={gallery.open}
        items={gallery.items}
        index={gallery.index}
        onClose={() => setGallery((g) => ({ ...g, open: false }))}
        onChangeIndex={(i) => setGallery((g) => ({ ...g, index: i }))}
      />
    </MonitoringLayout>
  );
}
